import { useId, useState } from 'react';
import { Link } from 'react-router';
import type { SupportAccessGrant } from '@traceio-org/platform-client';
import { EmptyPageState, NavigationGlyph, PageHeading, PageSection } from '@traceio-org/ui';
import { AdminPage } from '../components/AdminPage';
import { AdminProblem } from '../components/AdminProblem';
import { LoadingBlock, SavedNotice, useNow } from '../components/feedback';
import { ReasonField, reasonError } from '../components/fields';
import { GrantsTable, SCOPE_LABELS } from '../components/grantsTable';
import { WriteDialog, useDialogTarget } from '../components/WriteDialog';
import { useAbilities, useAdmin, useStaff } from '../context';
import { formatDateTime, formatRemaining } from '../format';
import { useAdminRead } from '../useAdminRead';
import { isGrantActive } from './organization/scope';

function OrganizationLink({ organizationId }: { organizationId: string }) {
  return (
    <Link className="admin-link" to={`/organizations/${organizationId}`}>
      <code>{organizationId}</code>
    </Link>
  );
}

/** A grant in force, as unmistakable as the banner on the organization's pages. */
function ActiveGrant({ grant, now, onEnd }: { grant: SupportAccessGrant; now: number; onEnd: () => void }) {
  const headingId = useId();
  return (
    <section className="admin-access-banner" aria-labelledby={headingId} data-scope={grant.scope}>
      <span className="admin-access-banner__icon" aria-hidden="true">
        <NavigationGlyph name="Headphones" />
      </span>
      <div className="admin-access-banner__body">
        <h3 id={headingId}>{SCOPE_LABELS[grant.scope]} access is on</h3>
        <p>
          Organization <OrganizationLink organizationId={grant.organizationId} />, until{' '}
          <strong>
            <time dateTime={grant.expiresAt}>{formatDateTime(grant.expiresAt)}</time>
          </strong>{' '}
          ({formatRemaining(Date.parse(grant.expiresAt) - now)} left). Reason: {grant.reason}
          {grant.ticketRef ? ` (ticket ${grant.ticketRef})` : ''}.
        </p>
      </div>
      <button type="button" className="admin-action" onClick={onEnd}>
        End this support access
      </button>
    </section>
  );
}

/**
 * My support access (/support-access): the signed-in staff member's own grants, the ones in force first (each
 * with a way to end it early) and the earlier ones below. Grants are requested from an organization's Support
 * access section, where the reason and ticket belong to that organization.
 */
export function MySupportAccessPage() {
  const { api } = useAdmin();
  const abilities = useAbilities();
  const { person } = useStaff();
  // Re-rendered by the tick; each render reads the clock afresh (a grant that just started is in force).
  const now = Math.max(useNow(15_000), Date.now());
  const read = useAdminRead('my-support-access', (signal) =>
    api.listSupportAccessGrants({ status: 'all' }, signal),
  );
  const mine = read.failure
    ? null
    : (read.data?.items.filter((grant) => grant.staffUserId === person.userId) ?? null);
  const active = (mine ?? []).filter((grant) => isGrantActive(grant, now));
  const earlier = (mine ?? []).filter((grant) => !isGrantActive(grant, now));

  const [notice, setNotice] = useState<string | null>(null);
  const dialog = useDialogTarget<SupportAccessGrant>();
  const [reason, setReason] = useState('');
  const [reasonProblem, setReasonProblem] = useState<string | null>(null);

  let content;
  if (read.failure) {
    content = <AdminProblem failure={read.failure} subject="your support access" onRetry={read.retry} />;
  } else if (!mine) {
    content = <LoadingBlock label="Loading your support access" />;
  } else if (mine.length === 0) {
    content = (
      <EmptyPageState
        icon={<NavigationGlyph name="Headphones" />}
        title="You have no support access grants"
        description={
          abilities.requestSupportAccess
            ? "Request one from an organization's Support access section when you need to look at its data."
            : 'Your staff role cannot request support access.'
        }
      />
    );
  } else {
    content = (
      <>
        <PageSection title="In force now">
          {active.length === 0 ? (
            <p className="admin-muted">None of your grants is in force now.</p>
          ) : (
            <div className="admin-stack">
              {active.map((grant) => (
                <ActiveGrant
                  key={grant.id}
                  grant={grant}
                  now={now}
                  onEnd={() => {
                    setReason('');
                    setReasonProblem(null);
                    dialog.show(grant);
                  }}
                />
              ))}
            </div>
          )}
        </PageSection>
        <PageSection title="Earlier grants">
          {earlier.length === 0 ? (
            <p className="admin-muted">No earlier grant.</p>
          ) : (
            <GrantsTable
              grants={earlier}
              caption="Earlier grants"
              staffUserId={person.userId}
              now={now}
              busy={read.loading}
              first={{
                label: 'Organization',
                render: (grant) => <OrganizationLink organizationId={grant.organizationId} />,
              }}
            />
          )}
        </PageSection>
      </>
    );
  }

  return (
    <AdminPage title="My support access">
      <PageHeading
        title="My support access"
        description="Your time-limited access to organizations' data. Every read during a grant is recorded with its reason."
      />
      <SavedNotice message={notice} />
      {content}
      {dialog.target ? (
        <WriteDialog
          key={dialog.key}
          open={dialog.open}
          onClose={dialog.close}
          title="End this support access now?"
          description="You stop reading the organization's data at once."
          submitLabel="End support access"
          onSubmit={async () => {
            const problem = reason.trim() ? reasonError(reason) : null;
            setReasonProblem(problem);
            if (problem) return 'invalid';
            await api.revokeSupportAccess(dialog.target!.id, reason);
            return undefined;
          }}
          onSaved={() => {
            setNotice('Your support access has ended.');
            read.retry();
          }}
        >
          <ReasonField optional value={reason} onChange={setReason} error={reasonProblem} />
        </WriteDialog>
      ) : null}
    </AdminPage>
  );
}
